import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { formatStartHole, formatThru, formatToPar, hasTeedOff } from '../lib/format'
import { formatTime, localZoneLabel } from '../lib/datetime'
import { useMediaQuery } from '../lib/useMediaQuery'
import { TournamentChat } from '../components/TournamentChat'
import { usePageTitle } from '../lib/usePageTitle'

interface TeamPlayerRow {
  dg_id: number
  slot: number
  full_name: string
  country: string | null
  region: string | null
  rank_at_pick: number | null
  position: string | null
  total_to_par: number | null
  today_to_par: number | null
  thru: number | null
  status: string | null
  /** Aktuell rond, ISO. Null när DataGolf inte satt tiderna än. */
  tee_time: string | null
  start_hole: number | null
}

interface Group {
  id: string
  name: string
}

interface TeamRow {
  team_id: string
  display_name: string
  group_id: string | null
  rank: number | null
  total_to_par: number | null
  raw_to_par: number | null
  today_to_par: number | null
  cut_penalty: number
  wd_penalty: number
  players: TeamPlayerRow[]
}

interface TournamentRow {
  id: string
  name: string
  course: string | null
  status: string
  current_round: number
  cut_line: number | null
  cut_line_source: string | null
}

const STATUS_LABEL: Record<string, string> = {
  cut: 'CUT',
  wd: 'WD',
  nto: 'NTO',
}

/** Grönt under par, rött över. Samma färger som v1, och samma i alla celler. */
function tone(n: number | null | undefined): string {
  if (n === null || n === undefined) return ''
  if (n < 0) return ' score--under'
  if (n > 0) return ' score--over'
  return ''
}

export function Leaderboard() {
  const { id = '' } = useParams()
  const [tournament, setTournament] = useState<TournamentRow | null>(null)
  const [teams, setTeams] = useState<TeamRow[]>([])
  const [expanded, setExpanded] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pulse, setPulse] = useState(false)
  const [onCourse, setOnCourse] = useState(0)
  const [updatedAt, setUpdatedAt] = useState<string | null>(null)
  usePageTitle(tournament?.name)

  const [groups, setGroups] = useState<Group[]>([])
  /** null = alla anmälda. Sätts till det egna gänget när det laddats. */
  const [scope, setScope] = useState<string | null>(null)
  const [scopeChosen, setScopeChosen] = useState(false)

  // Brytpunkten är den bredd där v1:s tre spelarkolumner får plats utan att
  // namnen börjar klippas. Under den gäller den hopfällbara listan.
  const wide = useMediaQuery('(min-width: 1000px)')

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const load = useCallback(async () => {
    const [t, lb, ute, senast] = await Promise.all([
      supabase
        .from('tournaments')
        .select('id, name, course, status, current_round, cut_line, cut_line_source')
        .eq('id', id)
        .maybeSingle(),
      supabase
        .from('team_leaderboard')
        .select(
          'team_id, display_name, group_id, rank, total_to_par, raw_to_par, today_to_par, cut_penalty, wd_penalty, players',
        )
        .eq('tournament_id', id),
      // Hur många är faktiskt ute på banan? Ronddetekteringen ger senast
      // avslutade rond när ingen spelar, vilket är rätt som data men läses
      // som "rond 3 pågår" när rond 3 just tagit slut.
      supabase
        .from('live_scores')
        .select('dg_id', { count: 'exact', head: true })
        .eq('tournament_id', id)
        .gte('thru', 1)
        .lte('thru', 17),
      // När skrev synken senast? Workern stämplar varje rad vid varje varv,
      // så senaste updated_at är den faktiska synktidpunkten.
      supabase
        .from('live_scores')
        .select('updated_at')
        .eq('tournament_id', id)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle(),
    ])

    if (t.error) setError(t.error.message)
    else setTournament(t.data as TournamentRow)

    if (lb.error) setError(lb.error.message)
    else {
      const rows = (lb.data as unknown as TeamRow[]) ?? []
      // Lag utan placering sist — de har inte spelat än.
      rows.sort((a, b) => (a.rank ?? 9999) - (b.rank ?? 9999))
      setTeams(rows)
    }

    setOnCourse(ute.count ?? 0)
    setUpdatedAt((senast.data as { updated_at: string } | null)?.updated_at ?? null)
    setLoading(false)
  }, [id])

  useEffect(() => {
    void load()
  }, [load])

  // Det egna gänget är utgångsläget. Den globala tabellen finns kvar i
  // väljaren — men "hur ligger jag mot dem jag spelar mot" är frågan man
  // öppnar sidan med, och för den som hamnat i standardgänget är den
  // globala listan i praktiken samma sak.
  useEffect(() => {
    async function loadGroups() {
      const { data: sess } = await supabase.auth.getSession()
      const uid = sess.session?.user.id
      if (!uid) return

      // Filtret på user_id är inte en optimering. RLS på group_members
      // släpper igenom HELA gänget — utan filtret kommer en rad per medlem
      // tillbaka, och väljaren fick lika många identiska "Majortipset" som
      // gänget har medlemmar.
      const { data } = await supabase
        .from('group_members')
        .select('groups(id, name)')
        .eq('user_id', uid)
        .order('joined_at')

      // PostgREST typar den inbäddade relationen som en array även när den
      // är en-till-en, så platta ut båda formerna.
      const list = ((data ?? []) as unknown as Array<{ groups: Group | Group[] | null }>)
        .flatMap((r) => (Array.isArray(r.groups) ? r.groups : r.groups ? [r.groups] : []))

      setGroups(list)
      setScope((prev) => (scopeChosen ? prev : list[0]?.id ?? null))
    }
    void loadGroups()
  }, [scopeChosen])

  // Push i stället för poll. Scoring-workern skriver var minut; utan detta
  // hade varje klient fått fråga själv — v1:s kvotproblem i ny förpackning.
  useEffect(() => {
    const channel = supabase
      .channel(`leaderboard:${id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'live_scores', filter: `tournament_id=eq.${id}` },
        scheduleReload,
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'teams', filter: `tournament_id=eq.${id}` },
        scheduleReload,
      )
      .subscribe()

    // En rondsynk rör 147 rader. Att hämta om per rad vore vansinne — samla
    // ihop och gör en omladdning när det lugnat sig.
    function scheduleReload() {
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => {
        void load()
        setPulse(true)
        setTimeout(() => setPulse(false), 1200)
      }, 800)
    }

    return () => {
      if (timer.current) clearTimeout(timer.current)
      void supabase.removeChannel(channel)
    }
  }, [id, load])

  if (loading) return <div className="page"><p className="muted">Hämtar ställningen…</p></div>
  if (!tournament) return <div className="page"><p className="muted">Turneringen finns inte.</p></div>

  // Före start är current_round 0. Rond 1 är den man syftar på då.
  const round = tournament.current_round || 1

  /*
   * Placeringen räknas om inom vyn. Ett lag som ligger fyra totalt kan vara
   * tvåa i sitt gäng, och det är den siffran man vill se när man tittar på
   * gängets tabell. Delade placeringar följer den globala rankningen: är två
   * lag lika enligt hela regeluppsättningen är de lika här också.
   */
  const filtered = scope ? teams.filter((t) => t.group_id === scope) : teams
  let seen = 0
  let pos = 0
  let prevRank: number | null = null
  const shown = filtered.map((t) => {
    if (t.rank === null) return { ...t, pos: null as number | null }
    seen++
    if (t.rank !== prevRank) pos = seen
    prevRank = t.rank
    return { ...t, pos: pos as number | null }
  })

  return (
    <div className="page stack">
      <div>
        <p className="eyebrow">{tournament.course ?? 'Leaderboard'}</p>
        <h1>{tournament.name}</h1>
        <p className="muted">
          {/* Ronden är den SENAST AVSLUTADE när ingen är ute på banan — det
              är rätt som data, men "rond 3 pågår" är fel ord för en rond som
              just tagit slut. Är ingen mitt i en rond spelas ingenting. */}
          {tournament.status === 'live'
            ? onCourse > 0
              ? `Rond ${tournament.current_round} pågår · ${onCourse} ute på banan`
              : tournament.current_round < 4
                ? `Rond ${tournament.current_round} klar · väntar på rond ${tournament.current_round + 1}`
                : `Rond ${tournament.current_round} klar`
            : tournament.status === 'completed'
              ? 'Avslutad'
              : 'Inte startad'}
          {tournament.cut_line !== null && ` · cut ${formatToPar(tournament.cut_line)}`}
          {tournament.cut_line_source === 'projected' && ' (prognos)'}
          {updatedAt && ` · uppdaterad ${formatTime(updatedAt)} från DataGolf`}
          {pulse && <span className="live-dot" aria-label="uppdaterad" />}
        </p>
      </div>

      {error && <div className="notice notice--error">{error}</div>}

      {groups.length > 0 && teams.length > 0 && (
        <div className="lb__scope">
          <label htmlFor="scope">Tabell</label>
          <select
            id="scope"
            value={scope ?? ''}
            onChange={(e) => { setScope(e.target.value || null); setScopeChosen(true) }}
          >
            {groups.map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
            <option value="">Alla anmälda — globala tabellen</option>
          </select>
        </div>
      )}

      {teams.length === 0 && (
        <div className="card">
          <p className="muted">
            Inga lag att visa än. Andras lag blir synliga först när tävlingen startat.
          </p>
        </div>
      )}

      {teams.length > 0 && shown.length === 0 && (
        <div className="card">
          <p className="muted">
            Ingen i det här gänget har lämnat lag i turneringen. Byt till den globala
            tabellen för att se alla.
          </p>
        </div>
      )}

      {/* Snacket är en widget i högerkanten och ligger utanför flödet — se
          .chat i global.css. Ställningen får hela bredden; den är det sidan
          finns för. Panelen står sist i DOM av samma skäl: den som läser
          uppifrån ska möta ställningen först. */}
      <div className="lb">
        <div className="board">
        {shown.length > 0 && (
          <div className="board__row board__row--head">
            <span className="board__pos">#</span>
            <span className="board__name">{wide ? 'Lag & spelare' : 'Lag'}</span>
            <span className="board__score">R{round}</span>
            <span className="board__score">Total</span>
            {!wide && <span className="board__chevron" />}
          </div>
        )}

        {shown.map((t) => {
          // På bred skärm står alla lag utfällda, som i v1. Klickandet finns
          // bara där det behövs — på en telefon får inte nio lag à sex
          // spelare ligga öppna samtidigt.
          const open = wide || expanded === t.team_id

          const row = (
            <>
              <span className="board__pos">{t.pos ?? '–'}</span>
              <span className="board__name">
                {t.display_name}
                {(t.cut_penalty > 0 || t.wd_penalty > 0) && (
                  <span className="board__penalty">
                    {t.cut_penalty > 0 && `cut +${t.cut_penalty}`}
                    {t.cut_penalty > 0 && t.wd_penalty > 0 && ' · '}
                    {t.wd_penalty > 0 && `wd +${t.wd_penalty}`}
                  </span>
                )}
              </span>
              {/* Rondsiffran räknas bara på spelare som fortfarande är med,
                  så den är tom innan någon i laget slagit ut. Rondnumret
                  står med: "-4" ensamt säger inte vilken rond det gäller. */}
              <span className={`board__score board__score--today${tone(t.today_to_par)}`}>
                {t.today_to_par !== null && (
                  <>
                    <span className="board__round">R{round}</span>
                    {formatToPar(t.today_to_par)}
                  </>
                )}
              </span>
              <span className={`board__score${tone(t.total_to_par)}`}>
                {formatToPar(t.total_to_par)}
              </span>
            </>
          )

          return (
            <div key={t.team_id} className={`board__team${open ? ' board__team--open' : ''}`}>
              {wide ? (
                <div className="board__row board__row--static">{row}</div>
              ) : (
                <button
                  className="board__row"
                  onClick={() => setExpanded(expanded === t.team_id ? null : t.team_id)}
                  aria-expanded={open}
                >
                  {row}
                  <span className="board__chevron">{open ? '−' : '+'}</span>
                </button>
              )}

              {open && (
                <div className="board__detail">
                  <div className="board__player board__player--head">
                    <span className="board__player-pos">Pos</span>
                    <span className="board__player-name">Spelare</span>
                    <span className="board__player-thru">Hål</span>
                    <span className="board__player-today">Idag</span>
                    <span className="board__player-total">Total</span>
                  </div>

                  {t.players.map((p) => {
                    const out = STATUS_LABEL[p.status ?? '']
                    const started = hasTeedOff(p.thru, p.status)
                    return (
                      <div key={p.dg_id} className={`board__player${out ? ' board__player--out' : ''}`}>
                        <span className="board__player-pos">
                          {out ?? p.position ?? '–'}
                        </span>
                        <span className="board__player-name">
                          {p.full_name}
                          <span className="board__player-meta"> {p.country}</span>
                        </span>
                        {/* Hålkolumnen bär tee-tiden innan spelaren slagit ut.
                            Totalen får stå kvar — från rond 2 och framåt har
                            en spelare som väntar på sin tid ändå en score.
                            För cutade och avhoppade står den tom: de har
                            varken hål kvar eller en tid att gå ut på, och
                            statusen syns redan i positionskolumnen. */}
                        {out ? (
                          <span className="board__player-thru" />
                        ) : started ? (
                          <span className="board__player-thru">{formatThru(p.thru)}</span>
                        ) : (
                          <span
                            className="board__player-thru board__player-tee"
                            title={`Tee-tid rond ${tournament.current_round || 1}, ${localZoneLabel()}`}
                          >
                            {p.tee_time
                              ? `${formatTime(p.tee_time)}${formatStartHole(p.start_hole)}`
                              : '–'}
                          </span>
                        )}
                        <span className={`board__player-today${started ? tone(p.today_to_par) : ''}`}>
                          {started && !out ? formatToPar(p.today_to_par) : ''}
                        </span>
                        <span className={`board__player-total${tone(p.total_to_par)}`}>
                          {formatToPar(p.total_to_par)}
                        </span>
                      </div>
                    )
                  })}

                  {/* Råscore-raden är borttagen på Marcus begäran — den tog
                      plats utan att tillföra något så länge inga cutregler
                      justerar totalen. Straffen syns redan vid lagnamnet.
                      Återinför uppdelningen när cut-straffen börjar användas. */}
                </div>
              )}
            </div>
          )
        })}
        </div>

        <TournamentChat tournamentId={id} />
      </div>
    </div>
  )
}
